/**
 * Layer 6 — intel & units: counters for own/allied units and enemy contacts.
 *
 *  - Counter sprites come from the atlas (one per style × symbol × colour × variant, drawn once);
 *    the style (illustrated / NATO) and the language switch live via `setStyle` → `restyle`.
 *  - Positions: a unit sits between `hex` and `path[0]` by `moveProgress`; on screen it is
 *    interpolated between the previous and the current view over `tickMs`, so marches are
 *    continuous at every game speed.
 *  - Stationary stacks fan out like stacked cardboard counters.
 *  - Strength (ink) and organisation (green → amber → red) bars under own and assessed counters.
 *  - Ghosts (remembered contacts): dashed frame, faded with age, an age tag ("6h", "2d") and a
 *    heading tick; unidentified contacts show "?"; unmasked decoys carry a DECOY stamp.
 *  - Selection: brass outline with a slow pulse; hover: dashed brass outline; encircled units
 *    (in a Kessel) a red dashed ring; routed units sit askew.
 *  - Replay "truth" (a faction's perspective): enemy units where they really stood, faint and
 *    behind everything, unless the staff saw them right there (decoys shown unmasked).
 * Everything is one instanced draw, rebuilt per frame from a list prepared once per view.
 */
import type { FactionId, HexGrid, IntelContact, PlayerView, Unit, UnitType } from '@friction/engine';
import { Container, type Texture } from 'pixi.js';
import type { UnitStyle } from '../../app/settings';
import { getLocale } from '../../i18n/locale';
import {
  COUNTER_H,
  COUNTER_W,
  type CounterVariant,
  counterGlyph,
  FRAME_CX,
  FRAME_CY,
  FRAME_W,
} from '../atlas/counters';
import type { Glyph, MarkerGlyphs } from '../atlas/glyphs';
import type { Atlas } from '../gfx/atlas';
import { QuadBatch, Scale } from '../gfx/quads';
import { mixColor, PAL } from '../palette';

interface Counter {
  id: number;
  contact: boolean;
  /** A replay "truth" counter: not clickable, drawn faintly behind the staff's picture. */
  truth: boolean;
  own: boolean;
  /** What the sprite shows (kept so a style switch can redraw it without a new view). */
  look: [type: UnitType | null, color: number, variant: CounterVariant];
  glyph: Glyph;
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  /** Stack offset in layout units. */
  sx: number;
  sy: number;
  alpha: number;
  strength: number;
  org: number;
  age: string | null;
  heading: number;
  routed: boolean;
  encircled: boolean;
  /** Unique designation printed beside the frame ("39", "II"), or null when unknown. */
  desig: string | null;
}

/** Css px per counter layout unit at a given zoom (readable at whole-map zoom, capped close up). */
export const counterScale = (zoom: number): number => Math.min(1.25, Math.max(0.34, zoom * 0.72));

const STACK_STEP = 4.5;
const TELEPORT = 140;
const TRUTH_ALPHA = 0.36;

/** "39th Infantry Division" → "39", "II Army HQ" → "II"; null for anything else. */
export function designationOf(name: string | null): string | null {
  if (!name) return null;
  const m = /^(\d+)(?:st|nd|rd|th)?\b/.exec(name) ?? /^([IVX]+)\b/.exec(name);
  return m ? m[1] : null;
}

/** Ghost age tag: "6h", "2d" — German "6h", "2T" (Tag); the tag is too small for "Std.". */
function formatAge(hours: number): string {
  if (hours < 24) return `${Math.max(1, hours)}h`;
  return `${Math.floor(hours / 24)}${getLocale() === 'de' ? 'T' : 'd'}`;
}

export function unitPos(grid: HexGrid, u: Pick<Unit, 'hex' | 'path' | 'moveProgress'>): [number, number] {
  const x = grid.cx(u.hex);
  const y = grid.cy(u.hex);
  if (!u.path.length || u.path[0] < 0) return [x, y];
  const t = Math.min(1, Math.max(0, u.moveProgress));
  return [x + (grid.cx(u.path[0]) - x) * t, y + (grid.cy(u.path[0]) - y) * t];
}

function orgColor(org: number): number {
  return org > 0.55
    ? mixColor(PAL.amber, PAL.ok, Math.min(1, (org - 0.55) / 0.3))
    : mixColor(PAL.danger, PAL.amber, Math.max(0, (org - 0.15) / 0.4));
}

export class UnitsLayer {
  readonly container = new Container({ label: 'units' });
  private readonly batch: QuadBatch;
  private readonly atlas: Atlas;
  private readonly grid: HexGrid;
  private readonly glyphs: MarkerGlyphs;
  private readonly tags: Map<string, Glyph>;
  private counters: Counter[] = [];
  /** World positions from the last frame, parallel to `counters` (hit tests, box select). */
  private posX = new Float32Array(0);
  private posY = new Float32Array(0);
  private unit = 1;
  /** Resting positions of the previous frame's truth counters (their interpolation start). */
  private truthPos = new Map<number, [number, number]>();
  private style: UnitStyle = 'illustrated';
  /** Style + language the counter sprites were drawn in. */
  private styleKey = '';
  selected = new Set<number>();
  inspected: number | null = null;
  hoverKey: string | null = null;

  constructor(
    grid: HexGrid,
    atlas: Atlas,
    glyphs: MarkerGlyphs,
    tags: Map<string, Glyph>,
    atlasTexture: Texture,
  ) {
    this.grid = grid;
    this.atlas = atlas;
    this.glyphs = glyphs;
    this.tags = tags;
    this.batch = new QuadBatch('counters', atlasTexture, 2048);
    this.container.addChild(this.batch.mesh);
  }

  /**
   * Counter style and language (checked every frame, cheap). On a change the atlas drops its
   * counter sprites and the counters on the map are redrawn in the new style at once — also
   * while the game is paused.
   */
  setStyle(style: UnitStyle, locale: string): void {
    const key = `${style}:${locale}`;
    if (key === this.styleKey) return;
    const first = this.styleKey === '';
    this.styleKey = key;
    this.style = style;
    if (first) return;
    this.atlas.resetVolatile();
    this.restyle();
  }

  /** Re-request every counter's sprite (after a style switch or an atlas volatile reset). */
  private restyle(): void {
    const v = this.atlas.volatileVersion;
    for (const c of this.counters) c.glyph = counterGlyph(this.atlas, this.style, ...c.look);
    // the zone overflowed and was reset while we drew: every sprite must be fetched again
    if (this.atlas.volatileVersion !== v)
      for (const c of this.counters) c.glyph = counterGlyph(this.atlas, this.style, ...c.look);
  }

  /** Prepare the counter list for a new view (called once per tick). */
  rebuild(
    view: PlayerView,
    prev: PlayerView | null,
    colors: ReadonlyMap<FactionId, number>,
    truth: readonly Unit[] | null = null,
  ): void {
    const g = this.grid;
    const list: Counter[] = [];
    const prevUnits = new Map<number, Unit>();
    const prevContacts = new Map<number, IntelContact>();
    if (prev && prev !== view) {
      for (const u of prev.units) prevUnits.set(u.id, u);
      for (const c of prev.contacts) prevContacts.set(c.unitId, c);
    }
    const color = (f: FactionId) => colors.get(f) ?? PAL.ink;
    const version = this.atlas.volatileVersion;
    const look = (
      type: UnitType | null,
      f: FactionId,
      variant: CounterVariant,
    ): Pick<Counter, 'look' | 'glyph'> => ({
      look: [type, color(f), variant],
      glyph: counterGlyph(this.atlas, this.style, type, color(f), variant),
    });
    const settle = (c: Counter) => {
      if (Math.hypot(c.x1 - c.x0, c.y1 - c.y0) > TELEPORT) {
        c.x0 = c.x1;
        c.y0 = c.y1;
      }
      list.push(c);
    };
    for (const u of view.units) {
      const [x1, y1] = unitPos(g, u);
      const p = prevUnits.get(u.id);
      const [x0, y0] = p ? unitPos(g, p) : [x1, y1];
      const decoy = u.type === 'decoy';
      settle({
        id: u.id,
        contact: false,
        truth: false,
        own: u.faction === view.faction,
        ...(decoy ? look(u.disguise ?? 'decoy', u.faction, 'decoy') : look(u.type, u.faction, 'solid')),
        x0,
        y0,
        x1,
        y1,
        sx: 0,
        sy: 0,
        alpha: 1,
        strength: u.strength,
        org: u.org,
        age: null,
        heading: Number.NaN,
        routed: u.status === 'routed',
        encircled: u.encircled,
        desig: designationOf(u.name),
      });
    }
    for (const c of view.contacts) {
      const x1 = g.cx(c.hex);
      const y1 = g.cy(c.hex);
      const p = prevContacts.get(c.unitId);
      const smooth = p?.visibleNow && c.visibleNow;
      const variant: CounterVariant = c.exposedDecoy ? 'exposed' : c.visibleNow ? 'solid' : 'ghost';
      const type = c.level >= 2 || c.exposedDecoy ? (c.type ?? null) : null;
      const age = view.tick - c.lastSeen;
      settle({
        id: c.unitId,
        contact: true,
        truth: false,
        own: false,
        ...look(type, c.faction, variant),
        x0: smooth ? g.cx(p.hex) : x1,
        y0: smooth ? g.cy(p.hex) : y1,
        x1,
        y1,
        sx: 0,
        sy: 0,
        alpha: c.visibleNow ? 1 : Math.max(0.3, 0.6 - (age / 96) * 0.3),
        strength: c.level >= 3 && c.strength !== null ? c.strength : -1,
        org: c.level >= 3 && c.org !== null ? c.org : -1,
        age: c.visibleNow ? null : formatAge(age),
        heading: !c.visibleNow && c.heading >= 0 ? (-c.heading * Math.PI) / 3 : Number.NaN,
        routed: false,
        encircled: false,
        desig: c.level >= 2 && !c.exposedDecoy ? designationOf(c.name) : null,
      });
    }
    const truthPos = new Map<number, [number, number]>();
    if (truth) {
      const seen = new Set<number>();
      for (const c of view.contacts) if (c.visibleNow) seen.add(c.unitId * 65536 + c.hex);
      const glide = prev !== null && prev !== view;
      for (const u of truth) {
        if (seen.has(u.id * 65536 + u.hex)) continue;
        const [x1, y1] = unitPos(g, u);
        truthPos.set(u.id, [x1, y1]);
        const [x0, y0] = (glide && this.truthPos.get(u.id)) || [x1, y1];
        const decoy = u.type === 'decoy';
        settle({
          id: u.id,
          contact: true,
          truth: true,
          own: false,
          ...(decoy ? look(u.disguise ?? 'decoy', u.faction, 'exposed') : look(u.type, u.faction, 'solid')),
          x0,
          y0,
          x1,
          y1,
          sx: 0,
          sy: 0,
          alpha: TRUTH_ALPHA,
          strength: -1,
          org: -1,
          age: null,
          heading: Number.NaN,
          routed: false,
          encircled: false,
          desig: null,
        });
      }
    }
    this.truthPos = truthPos;
    // fan out stationary stacks (same resting hex)
    const stacks = new Map<number, Counter[]>();
    for (const c of list) {
      if (c.x0 !== c.x1 || c.y0 !== c.y1) continue;
      const h = g.fromWorld(c.x1, c.y1);
      if (Math.hypot(g.cx(h) - c.x1, g.cy(h) - c.y1) > 1) continue;
      const s = stacks.get(h);
      if (s) s.push(c);
      else stacks.set(h, [c]);
    }
    for (const s of stacks.values()) {
      if (s.length < 2) continue;
      // contacts at the back, own units on top
      s.sort((a, b) => Number(a.own) - Number(b.own));
      const n = s.length;
      s.forEach((c, i) => {
        c.sx = (i - (n - 1) / 2) * STACK_STEP;
        c.sy = -(i - (n - 1) / 2) * STACK_STEP;
      });
    }
    list.sort(
      (a, b) =>
        Number(!a.truth) - Number(!b.truth) ||
        a.y1 + a.sy * 0.01 - (b.y1 + b.sy * 0.01) ||
        Number(a.own) - Number(b.own),
    );
    this.counters = list;
    if (this.atlas.volatileVersion !== version) this.restyle();
    this.posX = new Float32Array(list.length);
    this.posY = new Float32Array(list.length);
  }

  /** Per frame: interpolate and emit instances. `t` ∈ [0, 1] across the current tick. */
  frame(t: number, zoom: number, now: number, reducedMotion: boolean): void {
    const b = this.batch;
    const unit = counterScale(zoom) / zoom;
    this.unit = unit;
    b.setUnits(unit, unit);
    b.clear();
    const white = this.atlas.white;
    const pulse = reducedMotion ? 1 : 0.78 + 0.22 * Math.sin(now / 260);
    const C = Scale.Counter;
    for (let i = 0; i < this.counters.length; i++) {
      const c = this.counters[i];
      const x = c.x0 + (c.x1 - c.x0) * t;
      const y = c.y0 + (c.y1 - c.y0) * t;
      this.posX[i] = x;
      this.posY[i] = y;
      const ox = c.sx;
      const oy = c.sy;
      const key = (c.truth ? 't' : c.contact ? 'c' : 'u') + c.id;
      const selected = !c.contact && this.selected.has(c.id);
      const inspected = c.contact && !c.truth && this.inspected === c.id;
      if (selected || inspected) {
        const sel = selected ? this.glyphs.selection : this.glyphs.inspect;
        b.push(x, y, ox, oy, sel.w, sel.h, sel.frame, 0xffffff, pulse, C);
      }
      if (c.encircled) {
        // cut off: a red dashed ring around the counter (Kessel)
        const hv = this.glyphs.hover;
        b.push(x, y, ox, oy, hv.w + 6, hv.h + 6, hv.frame, PAL.danger, 0.95, C);
      }
      const rot = c.routed ? -0.14 : 0;
      b.push(
        x,
        y,
        ox - FRAME_CX,
        oy - FRAME_CY,
        COUNTER_W,
        COUNTER_H,
        c.glyph.frame,
        0xffffff,
        c.alpha,
        C,
        rot,
      );
      if (c.strength >= 0) {
        const bw = 46;
        const y1 = oy + 18.6;
        const y2 = oy + 21.4;
        b.push(x, y, ox, y1, bw + 1.2, 2.6, white, PAL.ink, 0.3 * c.alpha, C);
        b.push(
          x,
          y,
          ox - (bw * (1 - c.strength)) / 2,
          y1,
          bw * c.strength,
          1.8,
          white,
          PAL.ink,
          0.92 * c.alpha,
          C,
        );
        if (c.org >= 0) {
          b.push(x, y, ox, y2, bw + 1.2, 2.6, white, PAL.ink, 0.3 * c.alpha, C);
          b.push(x, y, ox - (bw * (1 - c.org)) / 2, y2, bw * c.org, 1.8, white, orgColor(c.org), c.alpha, C);
        }
      }
      if (c.desig) {
        // NATO: the unique designation sits at the lower left, outside the frame
        let tx = ox - FRAME_W / 2 - 2 - c.desig.length * 5.8;
        for (const ch of c.desig) {
          const gl = this.tags.get(ch);
          if (gl) b.push(x, y, tx + 3, oy + 10, gl.w, gl.h, gl.frame, 0xffffff, 0.92 * c.alpha, C);
          tx += 5.8;
        }
      }
      if (this.hoverKey === key && !selected) {
        const hv = this.glyphs.hover;
        b.push(x, y, ox, oy, hv.w, hv.h, hv.frame, PAL.brassLight, 0.95, C);
      }
      if (c.age) {
        let tx = ox + 27.5;
        for (const ch of c.age) {
          const gl = this.tags.get(ch);
          if (!gl) continue;
          b.push(x, y, tx, oy - 13, gl.w, gl.h, gl.frame, 0xffffff, 0.95, C);
          tx += 5.8;
        }
      }
      if (!Number.isNaN(c.heading)) {
        const ch = this.glyphs.chevron;
        const hx = Math.cos(c.heading);
        const hy = Math.sin(c.heading);
        const k = Math.min(28 / Math.max(Math.abs(hx), 1e-3), 20 / Math.max(Math.abs(hy), 1e-3));
        b.push(
          x + (ox + hx * k) * unit,
          y + (oy + hy * k) * unit,
          0,
          0,
          8,
          8,
          ch.frame,
          PAL.ink,
          0.75,
          C,
          c.heading,
        );
      }
    }
    b.commit();
  }

  /** Topmost counter under a world point. */
  hit(wx: number, wy: number): { unitId?: number; contactId?: number; key: string } | null {
    const u = this.unit;
    for (let i = this.counters.length - 1; i >= 0; i--) {
      const c = this.counters[i];
      if (c.truth) continue;
      const cx = this.posX[i] + c.sx * u;
      const cy = this.posY[i] + c.sy * u;
      if (Math.abs(wx - cx) <= 25 * u && Math.abs(wy - cy) <= 17 * u) {
        return c.contact ? { contactId: c.id, key: `c${c.id}` } : { unitId: c.id, key: `u${c.id}` };
      }
    }
    return null;
  }

  /** Own units whose counters lie inside a world rectangle. */
  ownInRect(x0: number, y0: number, x1: number, y1: number): number[] {
    const out: number[] = [];
    for (let i = 0; i < this.counters.length; i++) {
      const c = this.counters[i];
      if (!c.own) continue;
      const x = this.posX[i] + c.sx * this.unit;
      const y = this.posY[i] + c.sy * this.unit;
      if (x >= x0 && x <= x1 && y >= y0 && y <= y1) out.push(c.id);
    }
    return out;
  }

  /** Interpolated world position of a unit or contact (last frame), if drawn. */
  positionOf(id: number, contact = false): [number, number] | null {
    for (let i = 0; i < this.counters.length; i++) {
      const c = this.counters[i];
      if (c.id === id && c.contact === contact && !c.truth) return [this.posX[i], this.posY[i]];
    }
    return null;
  }

  destroy(): void {
    this.batch.destroy();
    this.container.destroy();
  }
}
